/**
 * Value Flow Edge Component for React Flow
 *
 * Renders curved edges with flow-type coloring:
 * - Value flows: Green
 * - Signal flows: Purple (animated)
 * - Internal flows: Cyan
 */
import { memo } from "react";
import { getBezierPath, type EdgeProps, EdgeLabelRenderer } from "reactflow";
import type { ValueFlowEdgeData } from "../../../../utils/schemaToReactFlow";
import { useHoverContext } from "../HoverContext";
import { EdgeHoverInfo } from "./EdgeHoverInfo";

/**
 * Edge color based on flow type
 */
const getEdgeColor = (flowType?: string): string => {
  switch (flowType) {
    case "signal":
      return "#a855f7"; // Purple for signals
    case "value":
      return "#22c55e"; // Green for value flows
    case "internal":
      return "#06b6d4"; // Cyan for internal flows
    default:
      return "#6b7280"; // Gray default
  }
};

/**
 * Selected edge color (brighter version)
 */
const getSelectedEdgeColor = (flowType?: string): string => {
  switch (flowType) {
    case "signal":
      return "#c084fc"; // Brighter purple
    case "value":
      return "#4ade80"; // Brighter green
    case "internal":
      return "#22d3ee"; // Brighter cyan
    default:
      return "#9ca3af"; // Brighter gray
  }
};

export const ValueFlowEdge = memo(
  ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    data,
    selected,
    markerEnd,
  }: EdgeProps<ValueFlowEdgeData>) => {
    const { hoveredEdgeId } = useHoverContext();
    const isHovered = hoveredEdgeId === id;

    const [edgePath, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      curvature: 0.25,
    });

    const baseColor = data?.color || getEdgeColor(data?.flowType);
    const strokeColor =
      selected || isHovered
        ? data?.color
          ? "#f9fafb"
          : getSelectedEdgeColor(data?.flowType)
        : baseColor;

    const isSignal = data?.flowType === "signal" && !data?.color;

    const labelScale = data?.labelScale ?? 1;
    const hideLabel = data?.hideLabel || labelScale < 0.85;
    const labelFontSize = Math.max(10, Math.round(12 * Math.min(labelScale, 1)));

    return (
      <>
        {/* Edge path */}
        <path
          id={id}
          className={`react-flow__edge-path ${isSignal ? "animate-pulse" : ""}`}
          d={edgePath}
          stroke={strokeColor}
          strokeWidth={selected || isHovered ? 3 : 2}
          fill="none"
          strokeDasharray={isSignal ? "5 3" : undefined}
          markerEnd={markerEnd}
        />

        {/* Invisible wider path for easier selection */}
        <path
          d={edgePath}
          stroke="transparent"
          strokeWidth={20}
          fill="none"
          className="react-flow__edge-interaction"
          style={{ cursor: "pointer" }}
        />

        {/* Hover info tooltip */}
        {data?.flowType && (
          <EdgeLabelRenderer>
            <EdgeHoverInfo
              flowType={data.flowType}
              label={data.label}
              isHovered={isHovered}
              x={labelX}
              y={labelY}
            />
          </EdgeLabelRenderer>
        )}

        {/* Optional edge label */}
        {data?.label && !hideLabel && (
          <EdgeLabelRenderer>
            <div
              style={{
                position: "absolute",
                transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                pointerEvents: "none",
              }}
              className={`
                px-2 py-0.5 rounded text-xs font-medium
                ${selected || isHovered ? "bg-sa-border" : "bg-sa-surface-hover/90"}
                border border-sa-border
              `}
            >
              <span
                style={{ color: data?.labelColor || strokeColor, fontSize: `${labelFontSize}px` }}
              >
                {data.label}
              </span>
            </div>
          </EdgeLabelRenderer>
        )}
      </>
    );
  },
);

ValueFlowEdge.displayName = "ValueFlowEdge";
